import axiosInstance from "../../services/axiosInstance";
import { apiBase, errorMessage, errorDetails } from "../helpdesk/api";

// Project Management API (/pm). Same CRM axios client (token + 401 handling)
// as the rest of the app.

const base = "/pm";
const unwrap = (res) => res.data?.data;
const get = (url, params) => axiosInstance.get(`${base}${url}`, { params }).then(unwrap);
const post = (url, body) => axiosInstance.post(`${base}${url}`, body ?? {}).then(unwrap);
const patch = (url, body) => axiosInstance.patch(`${base}${url}`, body).then(unwrap);
const put = (url, body) => axiosInstance.put(`${base}${url}`, body).then(unwrap);
const del = (url, body) => axiosInstance.delete(`${base}${url}`, { data: body }).then((r) => r.data);
const withMeta = (url, params) => axiosInstance.get(`${base}${url}`, { params }).then((r) => r.data);
const upload = (url, files, extra = {}) => {
  const fd = new FormData();
  [...files].forEach((f) => fd.append("files", f));
  Object.entries(extra).forEach(([k, v]) => v !== undefined && v !== null && fd.append(k, v));
  return axiosInstance.post(`${base}${url}`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(unwrap);
};
const blob = (url, params) => axiosInstance.get(`${base}${url}`, { params, responseType: "blob" }).then((r) => r.data);
const q = (query) => (query ? { query: JSON.stringify(query) } : {});

export { errorMessage, errorDetails };

export const pm = {
  meta: () => get("/meta"),
  invitePerson: (body) => post("/people/invite", body),
  createTeam: (body) => post("/teams", body),
  myWork: () => get("/my-work"),
  recent: () => get("/recent"),
  search: (text) => get("/search", { q: text }),

  // projects
  projects: (params) => get("/projects", params),

  // support tickets <-> issues
  createIssueFromTicket: (pid, body) => post(`/projects/${pid}/issues/from-ticket`, body),
  ticketIssues: (ticketId) => get(`/support/tickets/${ticketId}/issues`),
  issueTickets: (id) => withMeta(`/issues/${id}/support-tickets`),
  linkTicket: (id, ticket) => post(`/issues/${id}/support-tickets`, { ticket }),
  unlinkTicket: (id, ticketId) => del(`/issues/${id}/support-tickets/${ticketId}`),
  project: (id) => get(`/projects/${id}`),
  createProject: (body) => post("/projects", body),
  updateProject: (id, body) => patch(`/projects/${id}`, body),
  archiveProject: (id, on) => post(`/projects/${id}/${on ? "archive" : "unarchive"}`),
  deleteProject: (id, body) => del(`/projects/${id}`, body),
  trashedProjects: () => get("/trash/projects"),
  restoreProject: (id) => post(`/projects/${id}/restore`),
  trashedIssues: (id) => get(`/projects/${id}/trash`),
  overview: (id) => get(`/projects/${id}/overview`),
  health: (id) => get(`/projects/${id}/health`),
  activity: (id, params) => withMeta(`/projects/${id}/activity`, params),
  members: (id) => get(`/projects/${id}/members`),
  assignees: (id) => get(`/projects/${id}/assignees`),
  addMember: (id, body) => post(`/projects/${id}/members`, body),
  updateMember: (id, mid, body) => patch(`/projects/${id}/members/${mid}`, body),
  removeMember: (id, mid) => del(`/projects/${id}/members/${mid}`),

  // issues
  projectIssues: (id, query, params) => withMeta(`/projects/${id}/issues`, { ...q(query), ...params }),
  searchIssues: (query, params) => withMeta("/issues", { ...q(query), ...params }),
  issue: (id) => get(`/issues/${id}`),
  createIssue: (pid, body) => post(`/projects/${pid}/issues`, body),
  createBatch: (pid, items) => post(`/projects/${pid}/issues/batch`, { items }),
  updateIssue: (id, body) => patch(`/issues/${id}`, body),
  deleteIssue: (id) => del(`/issues/${id}`),
  restoreIssue: (id) => post(`/issues/${id}/restore`),
  archiveIssue: (id) => post(`/issues/${id}/archive`),
  cloneIssue: (id, body) => post(`/issues/${id}/clone`, body),
  moveIssue: (id, body) => post(`/issues/${id}/move`, body),
  transitions: (id) => get(`/issues/${id}/transitions`),
  transition: (id, body) => post(`/issues/${id}/transitions`, body),
  watch: (id, on) => (on ? post(`/issues/${id}/watch`) : del(`/issues/${id}/watch`)),
  addLink: (id, body) => post(`/issues/${id}/links`, body),
  removeLink: (id, linkId) => del(`/issues/${id}/links/${linkId}`),
  history: (id) => get(`/issues/${id}/history`),
  comments: (id, params) => withMeta(`/issues/${id}/comments`, params),
  addComment: (id, body) => post(`/issues/${id}/comments`, body),
  editComment: (id, cid, body) => patch(`/issues/${id}/comments/${cid}`, body),
  deleteComment: (id, cid) => del(`/issues/${id}/comments/${cid}`),
  react: (id, cid, emoji) => post(`/issues/${id}/comments/${cid}/react`, { emoji }),
  worklogs: (id) => get(`/issues/${id}/worklogs`),
  addWorklog: (id, body) => post(`/issues/${id}/worklogs`, body),
  editWorklog: (id, wid, body) => patch(`/issues/${id}/worklogs/${wid}`, body),
  deleteWorklog: (id, wid) => del(`/issues/${id}/worklogs/${wid}`),
  startTimer: (id) => post(`/issues/${id}/timer`),
  stopTimer: (body) => post("/timer/stop", body),
  myTimer: () => get("/timer"),
  uploadAttachments: (id, files) => upload(`/issues/${id}/attachments`, files),
  bulk: (ids, op, params) => post("/issues/bulk", { ids, op, params }),
  rank: (pid, body) => post(`/projects/${pid}/rank`, body),
  importIssues: (pid, rows, dryRun) => post(`/projects/${pid}/import`, { rows, dryRun }),
  importData: (body) => post("/import/issues", body),
  resetSummary: () => get("/reset"),
  resetWorkspace: (confirm) => post("/reset", { confirm }),
  exportIssues: (pid, query) => blob(pid ? `/projects/${pid}/export` : "/issues/export", q(query)),
  issueAi: (id, mode) => post(`/issues/${id}/ai/${mode}`),

  // agile
  backlog: (pid, params) => get(`/projects/${pid}/backlog`, params),
  sprints: (pid, params) => get(`/projects/${pid}/sprints`, params),
  activeSprints: () => get("/sprints/active"),
  createSprint: (pid, body) => post(`/projects/${pid}/sprints`, body),
  updateSprint: (sid, body) => patch(`/sprints/${sid}`, body),
  deleteSprint: (sid) => del(`/sprints/${sid}`),
  startSprint: (sid, body) => post(`/sprints/${sid}/start`, body),
  completeSprint: (sid, body) => post(`/sprints/${sid}/complete`, body),
  sprintIssues: (sid, body) => post(`/sprints/${sid}/issues`, body),
  sprintPlanning: (sid) => get(`/sprints/${sid}/planning`),
  burndown: (sid, unit) => get(`/sprints/${sid}/burndown`, { unit }),
  sprintAi: (sid) => post(`/sprints/${sid}/ai/summary`),
  boards: (pid) => get(pid ? `/projects/${pid}/boards` : "/boards"),
  boardList: (pid) => withMeta(pid ? `/projects/${pid}/boards` : "/boards"),
  board: (bid) => get(`/boards/${bid}`),
  boardIssues: (bid, params) => get(`/boards/${bid}/issues`, params),
  createBoard: (pid, body) => post(`/projects/${pid}/boards`, body),
  updateBoard: (bid, body) => patch(`/boards/${bid}`, body),
  deleteBoard: (bid) => del(`/boards/${bid}`),
  addColumn: (bid, body) => post(`/boards/${bid}/columns`, body),
  updateColumn: (bid, cid, body) => patch(`/boards/${bid}/columns/${cid}`, body),
  deleteColumn: (bid, cid) => del(`/boards/${bid}/columns/${cid}`),
  reorderColumns: (bid, columnIds) => put(`/boards/${bid}/columns/reorder`, { columnIds }),
  moveCard: (bid, body) => axiosInstance.post(`${base}/boards/${bid}/move`, body).then((r) => r.data),
  rankOnBoard: (bid, body) => post(`/boards/${bid}/issues/rank`, body),
  bulkOnBoard: (bid, ids, op, params) => post(`/boards/${bid}/issues/bulk`, { ids, op, params }),
  boardBacklog: (bid, params) => get(`/boards/${bid}/backlog`, params),
  boardSprints: (bid, params) => get(`/boards/${bid}/sprints`, params),
  createBoardSprint: (bid, body) => post(`/boards/${bid}/sprints`, body),
  boardVelocity: (bid) => get(`/boards/${bid}/velocity`),
  boardMetric: (bid, name, params) => get(`/boards/${bid}/${name}`, params),
  boardPrefs: (bid) => get(`/boards/${bid}/prefs`),
  setBoardPrefs: (bid, body) => put(`/boards/${bid}/prefs`, body),
  cancelSprint: (sid, body) => post(`/sprints/${sid}/cancel`, body),
  duplicateSprint: (sid) => post(`/sprints/${sid}/duplicate`),
  sprintCompletion: (sid) => get(`/sprints/${sid}/completion`),
  burnup: (sid, unit) => get(`/sprints/${sid}/burnup`, { unit }),
  removeFromSprint: (sid, issueId) => del(`/sprints/${sid}/issues/${issueId}`),
  versions: (pid) => get(`/projects/${pid}/versions`),
  createVersion: (pid, body) => post(`/projects/${pid}/versions`, body),
  updateVersion: (vid, body) => patch(`/versions/${vid}`, body),
  deleteVersion: (vid) => del(`/versions/${vid}`),
  releaseVersion: (vid, body) => post(`/versions/${vid}/release`, body),
  releaseNotesAi: (vid) => post(`/versions/${vid}/ai/notes`),
  components: (pid) => get(`/projects/${pid}/components`),
  createComponent: (pid, body) => post(`/projects/${pid}/components`, body),
  updateComponent: (id, body) => patch(`/components/${id}`, body),
  deleteComponent: (id) => del(`/components/${id}`),
  milestones: (pid) => get(`/projects/${pid}/milestones`),
  createMilestone: (pid, body) => post(`/projects/${pid}/milestones`, body),
  updateMilestone: (id, body) => patch(`/milestones/${id}`, body),
  deleteMilestone: (id) => del(`/milestones/${id}`),
  epics: (pid) => get(`/projects/${pid}/epics`),
  roadmap: (pid) => get(`/projects/${pid}/roadmap`),
  labels: (projectId) => get("/labels", { projectId }),

  // files & discussion
  files: (pid, params) => get(`/projects/${pid}/files`, params),
  uploadFiles: (pid, files, folder) => upload(`/projects/${pid}/files`, files, { folder }),
  deleteFile: (fid) => del(`/files/${fid}`),
  fileBlob: (fid) => blob(`/files/${fid}`),
  discussion: (pid, params) => withMeta(`/projects/${pid}/discussion`, params),
  postMessage: (pid, body) => post(`/projects/${pid}/discussion`, { body }),
  editMessage: (pid, mid, body) => patch(`/projects/${pid}/discussion/${mid}`, { body }),
  deleteMessage: (pid, mid) => del(`/projects/${pid}/discussion/${mid}`),
  projectAi: (pid) => post(`/projects/${pid}/ai/summary`),
  requirementsAi: (pid, text) => post(`/projects/${pid}/ai/requirements`, { text }),

  // filters, dashboards, reports
  filters: (projectId) => get("/filters", { projectId }),
  saveFilter: (id, body) => (id ? patch(`/filters/${id}`, body) : post("/filters", body)),
  deleteFilter: (id) => del(`/filters/${id}`),
  starFilter: (id, starred) => post(`/filters/${id}/star`, { starred }),
  dashboards: () => withMeta("/dashboards"),
  dashboard: (id) => get(`/dashboards/${id}`),
  saveDashboard: (id, body) => (id ? patch(`/dashboards/${id}`, body) : post("/dashboards", body)),
  deleteDashboard: (id) => del(`/dashboards/${id}`),
  widget: (id, wid, params) => get(`/dashboards/${id}/widgets/${wid}`, params),
  report: (name, params) => get(`/reports/${name}`, params),
  reportCsv: (name, params) => blob(`/reports/${name}`, { ...params, format: "csv" }),

  // configuration
  config: (path) => ({
    list: () => get(`/config/${path}`),
    create: (body) => post(`/config/${path}`, body),
    update: (id, body) => patch(`/config/${path}/${id}`, body),
    remove: (id) => del(`/config/${path}/${id}`),
  }),
  permissionCatalogue: () => get("/config/permissions"),
  access: () => get("/config/access"),
  updateAccess: (memberId, body) => patch(`/config/access/${memberId}`, body),
  settings: () => get("/config/settings"),
  updateSettings: (body) => patch("/config/settings", body),
  workflow: (id) => get(`/workflows/${id}`),
  saveWorkflow: (id, body) => put(`/workflows/${id}`, body),
  automation: (projectId, boardId) => get("/automation", { projectId, boardId }),
  saveAutomation: (id, body) => (id ? patch(`/automation/${id}`, body) : post("/automation", body)),
  deleteAutomation: (id) => del(`/automation/${id}`),
  automationRuns: (ruleId) => get("/automation-runs", { ruleId }),
};

/** URL an <img> can load for an uploaded file (signed, file-bound token). */
export const fileViewUrl = (viewToken) => `${apiBase()}/public/pm/files/${viewToken}`;

export const saveBlob = (data, filename) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Opens an authenticated file (download through axios, then a blob URL). */
export const openFile = async (file) => {
  const data = await pm.fileBlob(file._id);
  const url = URL.createObjectURL(data);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
